import { useEffect, useMemo, useRef, type ReactNode } from 'react';
import L from 'leaflet';
import { Circle, CircleMarker, MapContainer, Marker, TileLayer, ZoomControl, useMap } from 'react-leaflet';
import type { Device, LatLon } from '@findmy/shared';
import { markerState } from '../lib/device';
import { circleColour, markerIcon } from './markerIcon';
import { flyToVisible, outsideVisible, panelInsets } from './viewport';

const LONDON: [number, number] = [51.5072, -0.1276];

/** Zoom that fits a fix of this accuracy comfortably on screen. */
function zoomForAccuracy(metres: number): number {
  if (metres < 50) return 17;
  if (metres < 300) return 16;
  if (metres < 2_000) return 14;
  if (metres < 10_000) return 12;
  return 10;
}

function DeviceMarker({
  device,
  now,
  selected,
  onSelect,
}: {
  device: Device;
  now: number;
  selected: boolean;
  onSelect: (id: string) => void;
}) {
  const location = device.lastLocation;
  const state = markerState(device, now);
  const icon = useMemo(
    () => markerIcon({ id: device.id, name: device.name, type: device.type, state, selected }),
    [device.id, device.name, device.type, state, selected],
  );
  if (!location) return null;
  const center: [number, number] = [location.lat, location.lon];
  const approximate = location.source === 'ip';

  return (
    <>
      <Circle
        center={center}
        radius={location.accuracyM}
        pathOptions={{
          color: circleColour[state],
          weight: 1,
          fillOpacity: approximate ? 0.06 : 0.12,
          dashArray: approximate ? '6 6' : undefined,
        }}
        interactive={false}
      />
      <Marker
        position={center}
        icon={icon}
        title={device.name}
        alt={device.name}
        zIndexOffset={selected ? 1000 : 0}
        eventHandlers={{ click: () => onSelect(device.id) }}
      />
    </>
  );
}

/**
 * Fits every located device into view, refitting whenever a new one appears, until a
 * device is selected; then follows that device.
 */
function Camera({ devices, selectedId }: { devices: Device[]; selectedId: string | null }) {
  const map = useMap();
  const fittedIds = useRef('');

  const located = devices.filter((d) => d.lastLocation);
  const locatedIds = located.map((d) => d.id).join(',');
  useEffect(() => {
    if (selectedId || locatedIds === fittedIds.current || located.length === 0) return;
    fittedIds.current = locatedIds;
    const points = located.flatMap((d) =>
      d.lastLocation ? [L.latLng(d.lastLocation.lat, d.lastLocation.lon)] : [],
    );
    const { left, bottom } = panelInsets(map);
    map.fitBounds(L.latLngBounds(points), {
      paddingTopLeft: [left + 50, 60],
      paddingBottomRight: [60, bottom + 50],
      maxZoom: 15,
    });
  }, [locatedIds, map, selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  const selected = devices.find((d) => d.id === selectedId)?.lastLocation;
  const flownTo = useRef<string | null>(null);
  useEffect(() => {
    if (!selected || !selectedId) {
      flownTo.current = null;
      return;
    }
    const target = L.latLng(selected.lat, selected.lon);
    if (flownTo.current !== selectedId) {
      // Newly selected: fly there once.
      flownTo.current = selectedId;
      flyToVisible(map, target, Math.max(map.getZoom(), zoomForAccuracy(selected.accuracyM)));
      return;
    }
    // Already selected and moving: only pan if it's drifting out of view, so the map
    // isn't constantly animating (and never finishing loading tiles).
    if (outsideVisible(map, target)) flyToVisible(map, target, map.getZoom());
  }, [map, selectedId, selected?.lat, selected?.lon]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}

/**
 * The live map. With `children` (the history layer) the live markers are hidden and
 * the camera leaves the framing to the children.
 */
export function DeviceMap({
  devices,
  now,
  selectedId,
  onSelect,
  me,
  children,
}: {
  devices: Device[];
  now: number;
  selectedId: string | null;
  onSelect: (id: string) => void;
  me: LatLon | null;
  children?: ReactNode;
}) {
  const live = !children;
  const shown = live ? devices : [];
  return (
    <MapContainer center={LONDON} zoom={11} zoomControl={false} className="h-full w-full" worldCopyJump>
      <TileLayer
        url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
        maxZoom={19}
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      />
      <ZoomControl position="topright" />
      {shown.map((device) => (
        <DeviceMarker
          key={device.id}
          device={device}
          now={now}
          selected={device.id === selectedId}
          onSelect={onSelect}
        />
      ))}
      {me && (
        <CircleMarker
          center={[me.lat, me.lon]}
          radius={7}
          pathOptions={{ color: '#ffffff', weight: 3, fillColor: '#10b981', fillOpacity: 1 }}
        />
      )}
      <Camera devices={shown} selectedId={live ? selectedId : null} />
      {children}
    </MapContainer>
  );
}
